import { Controller } from "@hotwired/stimulus";
import { TabulatorFull as Tabulator } from "tabulator-tables";

export default class extends Controller {
    static targets = ["table"];

    connect() {
        this.reviewers = [];
        this.tableId = this.element.dataset.tableId;
        this.arrowIcon = this.element.dataset.arrowIcon;
        this.chevronIcon = this.element.dataset.chevronIcon;
        this.userIcon = this.element.dataset.userIcon;
        this.calendarIcon = this.element.dataset.calendarIcon;
        this.moneyIcon = this.element.dataset.moneyIcon;
        this.canAssign = this.element.dataset.canAssign === "true";
        this.loadReviewersAndTable();
    }

    async loadReviewersAndTable() {
        try {
            // Fetch reviewers
            const reviewersResponse = await fetch(`/payment_statement_reviews/fetch_reviewers`);
            this.reviewers = (await reviewersResponse.json()).reviewers;

            // Fetch table data
            const tableDataResponse = await fetch(`/payment_statement_reviews/fetch_table_data?table_type=${this.tableId}`);
            let tableData = (await tableDataResponse.json()).data;

            tableData = tableData.map(row => {
                const reviewer = this.reviewers.find(r => r.name === row.reviewer);
                return { ...row, reviewer: reviewer ? reviewer.id : null }; // Store reviewer ID
            });

            this.initializeTable(tableData);
        } catch (error) {
            console.error("Error loading data:", error);
        }
    }

    initializeTable(data) {
        let showActions = !(this.tableId === "unassigned");
        this.table = new Tabulator(this.tableTarget, {
            layout: "fitColumns",
            pagination: true,
            paginationSize: 15,
            columns: [
                { title: "ID", field: "id", visible: false },
                { title: "Finalized", field: "finalized", visible: false },
                {
                    title: "Organization",
                    field: "organization",
                    widthGrow: 1,
                    headerFilter: "input",
                    vertAlign: "middle",
                    formatter: (cell) => {
                        return `<span data-style="font-weight: 600;">${cell.getValue()}</span>`;
                    }
                },
                { title: "Uploaded at", field: "uploaded_at", widthGrow: 1, headerFilter: "input", vertAlign: "middle" },
                { title: "Status", field: "aasm_state", widthGrow: 1, headerFilter: "input", vertAlign: "middle", },
                {
                    title: "Days / Payments",
                    field: "combined",
                    widthGrow: 0.8,
                    hozAlign: "center",
                    headerHozAlign: "center",
                    headerSort: false,
                    headerFilter: false,
                    formatter: (cell) => {
                        const row = cell.getRow().getData();
                        const days = row.days_count;
                        const payments = row.payments_count;

                        return `<div class="flex flex-col items-center justify-center space-y-1 text-center w-full h-full">
                            <div class="flex items-center space-x-1">
                              ${this.calendarIcon}
                              <span>${days}</span>
                            </div>
                            <div class="flex items-center space-x-1">
                              ${this.moneyIcon}
                              <span>${payments}</span>
                            </div>
                          </div>`;
                    }
                },
                {
                    title: "Reviewer",
                    field: "reviewer",
                    widthGrow: 1.2,
                    vertAlign: "middle",
                    editor: this.tableId === "reviewed" || !this.canAssign ? false : "list",
                    headerFilter: "input",
                    editorParams: () => ({
                        values: this.reviewers.map(reviewer => ({
                            label: reviewer.name,
                            value: reviewer.id
                        })),
                    }),
                    formatter: (cell) => {
                        const reviewerId = cell.getValue();
                        const reviewer = this.reviewers.find(r => r.id === reviewerId);
                        const reviewerName = reviewer ? reviewer.name : "Click to assign";

                        return this.tableId === "reviewed" || !this.canAssign ? `
                            <div data-style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                                ${this.userIcon}<span>${reviewerName}</span>
                            </div>` :
                            `<div data-style="display: flex; align-items: center; gap: 8px; cursor: pointer;" data-tooltip="Expand">
                                ${this.userIcon}<span>${reviewerName}</span>${this.chevronIcon}
                            </div>`;
                    }
                },
                {
                    title: "",
                    visible: showActions,
                    field: "action",
                    widthGrow: 0.5,
                    vertAlign: "middle",
                    headerSort: false,
                    formatter: () => {
                        return `<span title="Go to Review">${this.arrowIcon}</span>`
                    },
                    cellClick: (e, cell) => {
                        const rowId = cell.getRow().getData().id;
                        window.location.href = `/payment_statement_reviews/${rowId}`;
                    }
                }
            ],
            data,
        });

        this.table.on("cellEdited", (cell) => {
            if (cell.getField() === "reviewer") {
                this.reviewerChanged(cell);
            }
        });
    }

    reviewerChanged(cell) {
        const row      = cell.getRow();
        const rowData  = row.getData();
        const rowId    = rowData.id;
        const reviewer = cell.getValue();

        // robust: we're on the "unassigned" table if that's the widget we rendered
        const fadeOut = this.tableId === "unassigned";

        fetch(`/payment_statement_reviews/${rowId}/assign_reviewer`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
            },
            body: JSON.stringify({ reviewer_id: reviewer })
        })
            .then((res) => {
                if (!res.ok) throw new Error(`Assign failed: ${res.status}`);
                return res.json();
            })
            .then(() => {
                if (fadeOut) {
                    // delete the actual row instance (avoids id/typing issues)
                    const el = row.getElement();
                    el.classList.add("fade-out");
                    setTimeout(() => row.delete(), 300);
                }
            })
            .catch((err) => console.error("Error assigning reviewer:", err));
    }
}
